import React, { useState, useEffect } from 'react';
import { Search, Plus, X, User, Phone, Mail, Loader2, Check } from 'lucide-react';
import { apiClient, API_ENDPOINTS } from '@dukene/shared';
import { type CustomerResponse } from '../../lib/api';

interface CustomerSearchProps {
  onSelect: (customer: CustomerResponse | null) => void;
  onClose: () => void;
}

export function CustomerSearch({ onSelect, onClose }: CustomerSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (query?: string) => {
    setLoading(true);
    try {
      const res = await apiClient.get<any>(API_ENDPOINTS.CUSTOMERS.LIST, {
        params: { search: query || searchQuery },
      });
      const rawList = Array.isArray(res) 
        ? res 
        : Array.isArray(res?.data) 
          ? res.data 
          : res?.data?.customers || res?.customers || res?.items || [];
      const mapped = rawList.map((c: any) => ({
        id: c.id,
        name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Walk-in Customer',
        phone: c.phone || c.phoneNumber || null,
        email: c.email || null,
        credit_balance: Number(c.creditBalance || c.credit_balance || 0),
      }));
      setCustomers(mapped);
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    fetchCustomers(val);
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await apiClient.post<any>(API_ENDPOINTS.CUSTOMERS.CREATE, {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      });
      const customerData = res?.data || res;
      const newCust: CustomerResponse = {
        id: customerData?.id || `cust-${Date.now()}`,
        name: customerData?.name || (customerData?.firstName ? `${customerData.firstName} ${customerData.lastName || ''}`.trim() : name.trim()),
        phone: customerData?.phone || phone.trim() || null,
        email: customerData?.email || email.trim() || null,
        credit_balance: 0,
      };
      onSelect(newCust);
      onClose();
    } catch (err) {
      // Fallback local select if offline or demo
      const newCust: CustomerResponse = {
        id: `cust-${Date.now()}`,
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        credit_balance: 0,
      };
      onSelect(newCust);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col max-h-[85vh]">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
          <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-emerald-600" />
            {isCreating ? 'Create New Customer' : 'Select Customer'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isCreating ? (
          <form onSubmit={handleCreateCustomer} className="p-5 space-y-4">
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Full Name *
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. John Doe"
                  className="w-full h-9 pl-9 pr-3 text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 text-slate-900 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Phone Number (for MPesa / Receipt)
              </label>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 0712345678"
                  className="w-full h-9 pl-9 pr-3 text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 text-slate-900 dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. john@example.com"
                  className="w-full h-9 pl-9 pr-3 text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 text-slate-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                Back to Search
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg inline-flex items-center gap-1.5"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save & Select
              </button>
            </div>
          </form>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name, phone or email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full h-8 pl-9 pr-3 text-xs bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-slate-900 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-lg inline-flex items-center gap-1 shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                New
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  onClose();
                }}
                className="w-full p-3 text-left rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between border border-dashed border-slate-300 dark:border-slate-700 transition-colors"
              >
                <div>
                  <p className="font-bold text-xs text-slate-900 dark:text-white">Walk-In Customer (Default)</p>
                  <p className="text-[11px] text-slate-500">Standard anonymous retail checkout</p>
                </div>
                <Check className="w-4 h-4 text-slate-400" />
              </button>

              {loading ? (
                <div className="py-8 text-center text-slate-400 flex items-center justify-center gap-2 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching customers...
                </div>
              ) : customers.length === 0 ? (
                <div className="py-8 text-center text-slate-500 text-xs">
                  No registered customers found. Click &quot;New&quot; to add one.
                </div>
              ) : (
                customers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onSelect(c);
                      onClose();
                    }}
                    className="w-full p-2.5 text-left rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border border-slate-200 dark:border-slate-800 flex items-center justify-between transition-colors"
                  >
                    <div>
                      <p className="font-bold text-xs text-slate-900 dark:text-white">{c.name}</p>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                        {c.phone && <span>📞 {c.phone}</span>}
                        {c.email && <span>✉️ {c.email}</span>}
                      </div>
                    </div>
                    {c.credit_balance && c.credit_balance > 0 ? (
                      <span className="text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded">
                        Credit: KSh {c.credit_balance.toLocaleString()}
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
